import {
  background,
  Box,
  BoxProps,
  Divider,
  Flex,
  HStack,
  Image,
  Skeleton,
  useTheme,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import React, {useEffect, useRef, useState} from 'react';
import {CommentOption, OPTIONS, USER_OPTIONS} from '../../../constants/commentOptions';
import useAuth, {User} from '../../../hooks/useAuth';
import {EventRSVPStatus} from '../../../hooks/useEvent';
import Icon from '../../Icon';
import PromptModal from '../../Modals/PromptModal';
import {TextG10, TextG12, TextG14, TextG16, TextG22} from '../../typography';
import UserAvatar from '../../UserAvatar';
import CommentInput from './CommentInput';
import {OPTIONS as EventOptions} from '../../../constants/eventOptions';
import {useNavigate} from 'react-router-dom';
import BottomSheetSelect from '../../BottomSheetSelect';
import {ToastMessage} from '../../toast';
import {find, orderBy} from 'lodash';
import RoseLogo from '../../../assets/logos/logo_rose2.png'; //'../../assets/logos/logo_rose2.png';
import RoleBadge from './RoleBadge';
import ImageGallery from 'react-image-gallery';
// import stylesheet if you're not already using CSS @import
import 'react-image-gallery/styles/css/image-gallery.css';
import {fetchChatMedia} from '../../../endpoints/api';
import Modal from '../../Modals/Modal';
import {TransformComponent, TransformWrapper} from 'react-zoom-pan-pinch';
import ExternalLinkModal from '../../Modals/ExternalLinkModal';
import ReplyPreview from './ReplyPreview';
import {useBlockUser} from '../../../hooks/useBlockUser';
import {getDisplayName} from '../../../utils/nameFormatters';
interface ChatCommentProps extends BoxProps {
  fetchUsersHook: any;
  comment: ChatComment;
  articleId: number | undefined;
  name?: string;
  isRSVPd?: boolean;
  isReply?: boolean;
  repliesShowingDefault?: boolean;
  hideOptions?: boolean;
  eventChatClosed?: boolean;
  setInputOpen?: any;
  numOfLines?: number;
  setInputHidden?: any;
  addComment: (content: string, parent_id?: string, imageURLs?: Array<any>) => void;
  updateComment: (
    commentId: string,
    content: string,
    parent_id?: string,
    imageURLs?: Array<any>,
    mediaToDelete?: ChatMedia[]
  ) => void;
  deleteComment: (commentId: string) => void;
  reportComment: (commentId: string) => void;
  helpfulComment: (commentId: string) => void;
  showUserRSVP?: boolean;
  onReplyClick?: (comment: ChatComment) => void;
  onScrollToParent?: (parentId: string) => void;
  isGroupStart?: boolean;
  isGroupEnd?: boolean;
  parentComment?: ChatComment;
}

export interface ChatComment {
  id: string;
  event_id: number;
  user_id: number;
  parent_id?: string;
  parent?: ChatComment; // Parent comment data for reply preview
  content: string;
  rsvp_status: EventRSVPStatus;
  is_featured: boolean;
  is_abusive: boolean;
  is_helpful: boolean;
  mentions?: Array<any>;
  status: number;
  created_at: string;
  updated_at: string;
  all_nested_replies?: Array<ChatComment>;
  user_image?: string;
  user: User;
  moreNestedComments?: boolean;
  isReply?: boolean;
  thread_media?: Array<ChatMedia>;
  hasImage?: boolean;
}

export interface ChatMedia {
  id: string;
  file_url: string;
  thumbnail_url: string;
}

function limitDepthOfReplies(comment: ChatComment, depthLimit: number): ChatComment {
  if (!comment.all_nested_replies || depthLimit === 0) {
    // If there are no nested replies or depth limit is reached, return the comment without nested replies.
    if (comment.all_nested_replies && comment.all_nested_replies.length > 0) {
      return {...comment, all_nested_replies: undefined, moreNestedComments: true};
    } else {
      return {...comment, moreNestedComments: false};
    }
  }

  // Recursively process and limit the depth of nested replies.
  const limitedReplies: ChatComment[] = [];
  let hasMoreNestedComments = false;

  for (const reply of comment.all_nested_replies) {
    const limitedReply = limitDepthOfReplies(reply, depthLimit - 1);
    if (limitedReply.moreNestedComments) {
      hasMoreNestedComments = true; // Set the flag to true if any nested comment was pruned
    }
    limitedReplies.push(limitedReply);
  }

  return {
    ...comment,
    all_nested_replies: limitedReplies,
    moreNestedComments: hasMoreNestedComments,
  };
}

const REPLY_DEPTH_LIMIT = 2;
export const MENTION_REGEXP = /@(\d+)\[(.*?)\]/g;

export const getUserHandle = (match, comment) => {
  let str;
  let id;
  let defaultHandle;

  while ((str = MENTION_REGEXP.exec(match)) !== null) {
    id = str[1];
    defaultHandle = str[2];
  }

  const mention = find(comment.mentions, (mention) => {
    return mention.id == id;
  });

  return {id: id, handle: (mention && mention.handle) || defaultHandle || ''};
};

export const linkifyText = (text: string) => {
  // URL regex that matches:
  // - http:// or https:// URLs
  // - www. URLs
  // - domain.tld patterns (like google.com, example.co.uk)
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?:\/[^\s]*)?)/g;

  return text.replace(urlRegex, (url) => {
    // Ensure the URL has a protocol
    let href = url;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      href = `https://${url}`;
    }
    return `<a href="#" data-external-url="${href}" class="chat-external-link" style="color: #3182ce; text-decoration: underline;">${url}</a>`;
  });
};

// Helper function to format user name as "First Name + Last Initial"
export const formatUserName = (user: User | undefined): string => {
  if (!user) return 'Unknown User';

  // for now just use user handle
  return user.handle;

  // const firstName = user.name || '';
  // const lastName = user.surname || '';

  // if (firstName && lastName) {
  //   return `${firstName} ${lastName.charAt(0)}.`;
  // } else if (firstName) {
  //   return firstName;
  // } else if (user.handle) {
  //   return user.handle;
  // }

  // return 'Unknown User';
};

function ColorizeText({text, regex, color}) {
  const parts = text.split(regex);

  return (
    <span>
      {parts.map((part, index) =>
        regex.test(part) ? (
          <span key={index} style={{color}}>
            <TextG12>{part}</TextG12>
          </span>
        ) : (
          <TextG12>{part}</TextG12>
        )
      )}
    </span>
  );
}

const ChatComment = ({
  comment,
  articleId,
  name,
  isReply,
  repliesShowingDefault = false,
  hideOptions = false,
  eventChatClosed = false,
  isRSVPd: isRSVPd,
  setInputOpen,
  setInputHidden,
  numOfLines,
  addComment,
  updateComment,
  deleteComment,
  reportComment,
  helpfulComment,
  fetchUsersHook,
  showUserRSVP,
  onReplyClick,
  onScrollToParent,
  isGroupStart = true,
  isGroupEnd = true,
  parentComment,
  ...rest
}: ChatCommentProps) => {
  const {user} = useAuth();
  const [replyInputOpen, setReplyInputOpen] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const [blockPromptOpen, setBlockPromptOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  // Initialize the hasImage state with the current value of comment.hasImage
  const [hasImage, setHasImage] = useState(!!comment.hasImage);
  const [externalLinkModalOpen, setExternalLinkModalOpen] = useState(false);
  const [externalUrl, setExternalUrl] = useState('');
  const [showActions, setShowActions] = useState(false);
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const commentContentRef = useRef<HTMLDivElement>(null);

  // Check if this is an optimistic comment (hasn't been saved to server yet)
  const isOptimistic = comment.id.startsWith('temp-');

  // Check if this message is from the current user
  const isOwnMessage = user?.id === comment.user_id;

  useEffect(() => {
    // Ensure that once hasImage becomes true, it stays true
    if (comment.hasImage && !hasImage) {
      setHasImage(true);
    }
  }, [comment.hasImage, hasImage]); // Track both comment.hasImage and hasImage to prevent flipping back

  useEffect(() => {
    // Handle clicks on external links
    const handleLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('chat-external-link')) {
        e.preventDefault();
        const url = target.getAttribute('data-external-url');
        if (url) {
          setExternalUrl(url);
          setExternalLinkModalOpen(true);
        }
      }
    };

    const contentElement = commentContentRef.current;
    if (contentElement) {
      contentElement.addEventListener('click', handleLinkClick);
    }

    return () => {
      if (contentElement) {
        contentElement.removeEventListener('click', handleLinkClick);
      }
    };
  }, [comment.content]);

  const theme = useTheme();
  const navigate = useNavigate();
  const blockUserMutation = useBlockUser();
  const [repliesShowing, setRepliesShowing] = useState(repliesShowingDefault);
  // const isRVSPd =
  //   event &&
  //   event.rsvp_status.status_value &&
  //   event.rsvp_status.status_value != EventRSVPStatus.NOT_INTERESTED;
  const isDeleted = comment.content == 'Message deleted by author';

  useEffect(() => {
    setInputHidden && setInputHidden(replyInputOpen);
  }, [replyInputOpen]);

  dayjs.updateLocale('en', {
    relativeTime: {
      future: 'in %s',
      past: '%s',
      s: function (number, withoutSuffix) {
        return withoutSuffix ? 'now' : 'now';
      },
      m: '1m',
      mm: '%dm',
      h: '1h',
      hh: '%dh',
      d: '1d',
      dd: '%dd',
      M: '1mth',
      MM: '%dmth',
      y: '1y',
      yy: '%dy',
    },
  });

  const handleAddComment = async (content: string, parent_id?: string, imageURLs?: Array<any>) => {
    addComment(content, parent_id, imageURLs);
    setReplyInputOpen(false);
  };

  const handleEditComment = async (
    content: string,
    parent_id?: string,
    imageURLs?: Array<any>,
    mediaToDelete?: ChatMedia[]
  ) => {
    updateComment(comment.id, content, parent_id, imageURLs, mediaToDelete);
    setEditOpen(false);
  };

  const handleDeleteComment = async () => {
    deleteComment(comment.id);
    setPromptOpen(false);
  };

  const handleReportComment = async () => {
    reportComment(comment.id);
    setPromptOpen(false);
    ToastMessage({
      duration: 4000,
      status: 'success',
      showStatusIcon: true,
      text: 'This message has been flagged and removed from Walkabout for now. Thank you!',
    });
  };

  const handleHelpfulComment = async () => {
    helpfulComment(comment.id);
    setPromptOpen(false);
  };

  const handleBlockUser = async () => {
    setBlockPromptOpen(false);
    try {
      await blockUserMutation.mutateAsync(comment.user_id);
      ToastMessage({
        duration: 3000,
        status: 'info',
        showStatusIcon: true,
        text: `${formatUserName(comment.user)} has been blocked`,
      });
    } catch (e) {
      ToastMessage({
        duration: 4000,
        status: 'error',
        showStatusIcon: true,
        text: 'Could not block user. Please try again.',
      });
    }
  };

  const handleOptionClicked = (option: CommentOption) => {
    switch (option) {
      case CommentOption.REPORT:
        handleReportComment();
        break;
      case CommentOption.HELPFUL:
        handleHelpfulComment();
        break;
      case CommentOption.EDIT:
        setEditOpen(true);
        break;
      case CommentOption.DELETE:
        setOptionsOpen(false);
        setPromptOpen(true);
        break;
    }
    setOptionsOpen(false);
  };

  const getIconStyle = (option) => {
    {
      return {
        fontSize: '10px',
        height: '10px',
        width: '10px',
        color: theme.colors[option.color] || option.color,
        fill: theme.colors[option.color] || option.color,
      };
    }
  };

  const images = comment.thread_media?.map((media) => {
    return {id: media.id, original: media.file_url, thumbnail: media.thumbnail_url};
  });

  const isOwnComment = user?.id === comment.user_id;
  const displayName = formatUserName(comment.user);

  return (
    <>
      {images && images.length > 0 && (
        <Modal
          closeButtonBorder
          closeButtonColor="#9b9b9b"
          isCentered
          modalPadding={'0px !important'}
          headerPadding={'0px !important'}
          parentCallback={() => setGalleryOpen(false)}
          show={galleryOpen}
          closeButton>
          <ImageGallery
            renderItem={(item) => (
              <Flex background={theme.colors.darkGrey}>
                <TransformWrapper>
                  <TransformComponent
                    contentStyle={{width: '95vw', height: '85vh'}}
                    wrapperStyle={{background: theme.colors.darkGrey}}>
                    <Image
                      fallbackStrategy="onError"
                      src={item.original}
                      alt="cover image"
                      width="100%"
                      height="100%"
                      objectFit="contain"
                      maxHeight="95vh"
                      background={theme.colors.darkGrey}
                    />
                  </TransformComponent>
                </TransformWrapper>
              </Flex>
            )}
            lazyLoad
            showBullets
            showNav={false}
            showPlayButton={false}
            showFullscreenButton={false}
            items={images}
          />
        </Modal>
      )}
      <BottomSheetSelect
        options={user?.id == comment.user_id ? USER_OPTIONS : OPTIONS}
        openModal={optionsOpen}
        onModalClose={() => setOptionsOpen(false)}
        onClick={handleOptionClicked}
      />
      <PromptModal
        title={`Are you sure you want to permanently delete your message from '${
          name ? name : ''
        }'?`}
        declineText={'Keep'}
        acceptText={'Delete'}
        declineCallback={() => setPromptOpen(false)}
        acceptCallback={(e) => handleDeleteComment()}
        isOpen={promptOpen}
      />
      <PromptModal
        title={`Block ${displayName}?`}
        description={
          "You won't see their messages or be able to interact with them on Walkabout."
        }
        declineText={'Cancel'}
        acceptText={'Block'}
        declineCallback={() => setBlockPromptOpen(false)}
        acceptCallback={() => handleBlockUser()}
        isOpen={blockPromptOpen}
      />

      {/* Avatar Modal */}
      <Modal
        contentStyle={{width: 'fit-content'}}
        show={avatarModalOpen}
        parentCallback={() => setAvatarModalOpen(false)}
        isCentered
        closeButton>
        <Flex direction="column" justify="center" align="center" p={4} gap={3}>
          <UserAvatar user={comment.user} avatar_image={comment.user_image} size="max" />
          {(comment.user?.name || comment.user?.handle) && (
            <TextG22 mt="0" fontWeight="700" textAlign="center">
              {getDisplayName(comment.user)}
            </TextG22>
          )}
          {comment.user?.handle && (
            <TextG14 mt="0" color="#757D84">
              @{comment.user.handle}
            </TextG14>
          )}
          {!isOwnComment && (
            <Box
              as="button"
              onClick={() => {
                setAvatarModalOpen(false);
                setBlockPromptOpen(true);
              }}
              fontFamily="Gordita"
              fontSize="14px"
              fontWeight="600"
              color="#E02F84"
              py={2}
              px={4}>
              Block user
            </Box>
          )}
        </Flex>
      </Modal>

      {/* New Simplified Comment Design */}
      <Box py={isGroupStart ? 3 : 0.5} w="100%" {...rest} mb={0}>
        {comment.is_featured === true && isGroupStart && (
          <Box
            display="inline-block"
            px={2}
            py={0.5}
            mb={2}
            bg={theme.colors.lightBlue}
            color={theme.colors.blue}
            borderRadius="4px"
            fontSize="10px"
            fontWeight="600"
            textTransform="uppercase">
            Announcement
          </Box>
        )}

        <Flex gap={3} w="100%" direction={isOwnMessage ? 'row-reverse' : 'row'}>
          {/* Avatar - only show on group start and hide for own messages */}
          {!isOwnMessage && isGroupStart ? (
            <Box flexShrink={0} cursor="pointer" onClick={() => setAvatarModalOpen(true)}>
              <UserAvatar
                user={comment.user}
                size="lg"
                avatar_image={comment.user_image}
                w="60px"
                h="60px"
              />
            </Box>
          ) : !isOwnMessage ? (
            <Box flexShrink={0} w="60px" h="60px" />
          ) : null}

          <Flex
            flexDir="column"
            flex={1}
            gap={0}
            alignItems={isOwnMessage ? 'flex-end' : 'flex-start'}
            mr={isOwnMessage ? '10px' : 0}>
            {/* Message Content */}
            {editOpen ? (
              <Box mt={2}>
                <CommentInput
                  fetchUsersHook={fetchUsersHook}
                  articleId={articleId}
                  onCloseClick={() => setEditOpen(false)}
                  onSaveClick={handleEditComment}
                  media={comment.thread_media}
                  defaultContent={comment.content.replace(MENTION_REGEXP, (match) =>
                    match.replace(
                      MENTION_REGEXP,
                      `@${getUserHandle(match, comment).id}[${
                        getUserHandle(match, comment).handle
                      }]`
                    )
                  )}
                />
              </Box>
            ) : (
              <>
                {/* Message bubble - contains header, content, and actions */}
                <Box
                  position="relative"
                  bg={isOwnMessage ? '#C8E6C9' : '#F5F5F5'}
                  borderRadius={
                    isOwnMessage
                      ? '7.5px 0px 7.5px 7.5px'
                      : '0px 7.5px 7.5px 7.5px'
                  }
                  px={3}
                  py={2}
                  mt={isGroupStart ? 1 : 0.5}
                  minW={isOwnMessage ? '80%' : '100%'}
                  maxW={isOwnMessage ? '80%' : '100%'}
                  onClick={() => {
                    // For non-group-end messages, toggle actions on tap
                    if (!isGroupEnd) {
                      setShowActions(!showActions);
                    }
                  }}
                  cursor={!isGroupEnd ? 'pointer' : 'default'}
                  sx={
                    isOwnMessage
                      ? {
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: '0',
                            right: '-7px',
                            width: '8px',
                            height: '13px',
                            background: '#C8E6C9',
                            clipPath: 'polygon(0 0, 0 100%, 100% 0)',
                            borderRadius: '0 0 0 3px',
                          },
                        }
                      : {
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: '0',
                            left: '-7px',
                            width: '8px',
                            height: '13px',
                            background: '#F5F5F5',
                            clipPath: 'polygon(0 0, 100% 0, 100% 100%)',
                            borderRadius: '0 0 3px 0',
                          },
                        }
                  }>
                  
                  {/* Header: Name and Badge - only show on group start */}
                  {isGroupStart && (
                    <Flex align="center" gap={1.5} mb={1} justify={'flex-start'}>
                      {isOwnMessage ? (
                        <TextG14
                          fontWeight="700"
                          color={theme.colors.lightBlack}
                          fontSize="15px"
                          noOfLines={1}>
                          You
                        </TextG14>
                      ) : (
                        <>
                          <TextG14
                            fontWeight="600"
                            color={
                              comment.user?.isWalkaboutEmployee
                                ? theme.colors.rose
                                : theme.colors.lightBlack
                            }
                            noOfLines={1}>
                            {comment.user?.name ? `${comment.user.name} (${displayName})` : displayName}
                          </TextG14>
                          <RoleBadge eventId={comment.event_id} commentUser={comment.user} />
                        </>
                      )}
                    </Flex>
                  )}

                  {/* Reply Preview - Show when this comment is replying to another */}
                  {comment.parent_id && comment.parent && (
                    <Box mb={2}>
                      <ReplyPreview
                        comment={comment.parent}
                        isInMessage={true}
                        onClick={() => {
                          if (onScrollToParent && comment.parent_id) {
                            onScrollToParent(comment.parent_id);
                          }
                        }}
                      />
                    </Box>
                  )}
                  
                  {/* Message content */}
                  <Box ref={commentContentRef}>
                    <TextG14
                      color={theme.colors.lightBlack}
                      whiteSpace="pre-line"
                      dangerouslySetInnerHTML={{
                        __html: linkifyText(
                          comment.content.replace(MENTION_REGEXP, (match) =>
                            match.replace(
                              MENTION_REGEXP,
                              `@${getUserHandle(match, comment).handle}`
                            )
                          )
                        ),
                      }}>
                      {}
                    </TextG14>
                  </Box>

                  {/* Images inside the bubble */}
                  {comment.thread_media && comment.thread_media.length > 0 && (
                    <HStack mt={2}>
                      {comment.thread_media.map((media) => (
                        <Image
                          onClick={() => setGalleryOpen(true)}
                          key={media.id}
                          src={media.thumbnail_url}
                          height="100px"
                          objectFit="contain"
                          boxShadow="md"
                          borderRadius="8px"
                          cursor="pointer"
                        />
                      ))}
                    </HStack>
                  )}

                  {hasImage && !comment.thread_media?.length && <Skeleton h="80px" w="80px" mt={2} />}

                  {/* Timestamp and Action Links */}
                  <Flex
                    justify={'space-between'}
                    align="center"
                    mt={1.5}>
                    <Flex gap={2} align="center">
                      {/* Timestamp - Always visible */}
                      <TextG12 color={isOwnMessage ? 'rgba(0, 0, 0, 0.5)' : theme.colors.lightestGrey} whiteSpace="nowrap">
                        {dayjs.utc(comment.created_at).fromNow()}
                        {comment.created_at !== comment.updated_at && !isDeleted && ' (edited)'}
                      </TextG12>

                      {/* Reply Button - Always visible */}
                      {!hideOptions &&
                        !comment.is_featured &&
                        !isOptimistic && (
                          <TextG12
                            whiteSpace="nowrap"
                            onClick={() => {
                              if (eventChatClosed) {
                                ToastMessage({
                                  status: 'error',
                                  text: 'This event has ended and the chat is closed.',
                                });
                              } else if (!isRSVPd) {
                                ToastMessage({
                                  status: 'error',
                                  text: 'You must RSVP to this event to participate in chat.',
                                });
                              } else if (onReplyClick) {
                                onReplyClick(comment);
                              }
                            }}
                            color={theme.colors.blue}
                            fontWeight="500"
                            cursor="pointer">
                            Reply
                          </TextG12>
                        )}
                    </Flex>

                    {/* Edit/Delete/Report buttons on the right - Always visible */}
                    {!isOptimistic && (
                      <Flex
                        gap={3}
                        align="center">
                        {/* Edit/Delete for own comments */}
                        {isOwnComment && !isDeleted && !hideOptions && !comment.is_featured && (
                          <>
                            <TextG12
                              color={theme.colors.grey}
                              fontWeight="400"
                              cursor="pointer"
                              onClick={() => setEditOpen(true)}
                              textDecoration="underline">
                              Edit
                            </TextG12>
                            <TextG12
                              color={theme.colors.grey}
                              fontWeight="400"
                              cursor="pointer"
                              onClick={() => setPromptOpen(true)}
                              textDecoration="underline">
                              Delete
                            </TextG12>
                          </>
                        )}

                        {/* Report for other users' comments */}
                        {!isOwnComment && !hideOptions && !comment.is_featured && (
                          <TextG12
                            color={theme.colors.grey}
                            fontWeight="400"
                            cursor="pointer"
                            onClick={() => handleReportComment()}
                            textDecoration="underline">
                            Report This Message
                          </TextG12>
                        )}
                      </Flex>
                    )}
                  </Flex>
                </Box>
              </>
            )}
          </Flex>
        </Flex>

        {/* <Divider mt={3} w="100%" /> */}
      </Box>

      <ExternalLinkModal
        url={externalUrl}
        show={externalLinkModalOpen}
        setShow={setExternalLinkModalOpen}
      />
    </>
  );
};

export default ChatComment;
